import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaWallet,
  FaRupeeSign,
  FaCalendarAlt,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaChartLine,
  FaUsers,
  FaDownload,
  FaFilter,
  FaSync,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import WalletSummaryCards from "../../components/Admin/WalletSummaryCards";
import RecentTransactionsTable from "../../components/Admin/RecentTransactionsTable";
import Pagination from "../../components/shared/Pagination";
import {
  getWalletSummary,
  getWalletTransactions,
} from "../../features/admin/adminSlice";

const WalletSummary = () => {
  const dispatch = useDispatch();
  const { walletSummary, walletTransactions, isLoading } = useSelector(
    (state) => state.admin,
  );

  const [timeFilter, setTimeFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [allStats, setAllStats] = useState(null);

  useEffect(() => {
    dispatch(getWalletSummary());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getWalletTransactions({ page, limit, type: 'admin_commission_credited', status: 'all' }));
  }, [dispatch, page, limit]);

  // Ensure current page stays within available range after data loads
  useEffect(() => {
    const pages = walletTransactions?.pagination?.pages || 0;
    if (pages && page > pages) {
      setPage(pages);
    }
    if (page < 1) setPage(1);
  }, [walletTransactions, page]);

  // Fetch full dataset for accurate totals
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const params = new URLSearchParams({ page: '1', limit: '10000', type: 'admin_commission_credited', status: 'all', sortBy: 'createdAt', sortOrder: 'desc' });
        const config = { headers: { Authorization: `Bearer ${token}` } };
        let res;
        try {
          res = await axios.get(`${API_URL}/wallet/admin/transactions?${params.toString()}`, config);
        } catch (err) {
          if (err.response && err.response.status === 404) {
            res = await axios.get(`${API_URL}/admin/wallet/transactions?${params.toString()}`, config);
          } else { throw err; }
        }
        const list = Array.isArray(res.data?.data) ? res.data.data : (res.data?.data?.transactions || []);
        const amount = list.reduce((s, t) => s + (Number(t?.transaction?.amount) || 0), 0);
        setAllStats({ total: list.length, amount, commissions: list.length, withdrawals: 0 });
      } catch (e) {
        // Ignore
      }
    };
    fetchAll();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(getWalletSummary()),
        dispatch(getWalletTransactions({ page, limit })),
      ]);
      toast.success("Wallet data refreshed successfully");
    } catch (error) {
      toast.error("Failed to refresh wallet data");
    } finally {
      setRefreshing(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getQuickStats = () => {
    if (!walletSummary) return [];

    return [
      {
        title: "Active Drivers",
        value: walletSummary.totalDrivers || 0,
        icon: FaUsers,
        color: "blue",
        description: "Drivers with wallet accounts",
      },
      {
        title: "Commission Rate",
        value: `${walletSummary.averageCommissionRate || 10}%`,
        icon: FaChartLine,
        color: "purple",
        description: "Average commission rate",
      },
      {
        title: "Pending Approvals",
        value: walletSummary.driversWithWithdrawalRequests || 0,
        icon: FaCheckCircle,
        color: "yellow",
        description: "Withdrawal requests pending",
      },
      {
        title: "Overdue Payments",
        value: walletSummary.driversWithNegativeWallet || 0,
        icon: FaExclamationTriangle,
        color: "red",
        description: "Drivers with negative balance",
      },
    ];
  };

  const quickStats = getQuickStats();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div className="flex items-center mb-4 sm:mb-0">
          <FaWallet className="text-3xl text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Wallet Management
            </h1>
            <p className="text-gray-600 mt-1">
              Monitor driver wallets, commissions, and transactions
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <FaSync className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>

          <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FaDownload className="mr-2" />
            Export
          </button>


        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {quickStats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
              </div>
              <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                <stat.icon className={`text-xl text-${stat.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* Main Wallet Summary Cards */}
      <WalletSummaryCards walletSummary={walletSummary} isLoading={isLoading} />

      {/* Financial Overview */}
      {walletSummary && Object.keys(walletSummary).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Breakdown */}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FaChartLine className="mr-2 text-green-600" />
              Revenue Breakdown
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  Total Commission Earned
                </span>
                <span className="text-lg font-bold text-green-700">
                  {formatCurrency(walletSummary.totalCommissionEarned || walletSummary.totalCommission || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  Today's Commission
                </span>
                <span className="text-lg font-bold text-blue-700">
                  {formatCurrency(walletSummary.commissionToday || walletSummary.todayCommission || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  This Month's Commission
                </span>
                <span className="text-lg font-bold text-purple-700">
                  {formatCurrency(walletSummary.commissionThisMonth || walletSummary.monthlyCommission || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Outstanding Balance */}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FaExclamationTriangle className="mr-2 text-red-600" />
              Outstanding Balances
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  Total Driver Balance
                </span>
                <span className="text-lg font-bold text-indigo-700">
                  {formatCurrency(walletSummary.totalDriverWalletBalance || walletSummary.totalPositiveBalance || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  Total Negative Balance
                </span>
                <span className="text-lg font-bold text-red-700">
                  -{formatCurrency(Math.abs(walletSummary.totalNegativeBalance || 0))}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                <span className="text-gray-700 font-medium">
                  Pending Withdrawals
                </span>
                <span className="text-lg font-bold text-yellow-700">
                  {walletSummary.driversWithWithdrawalRequests || walletSummary.pendingWithdrawals || 0} requests
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Commission-only summary header (from real dataset) */}
        {(() => {
          const baseList = Array.isArray(walletTransactions) ? walletTransactions : (walletTransactions?.transactions || []);
          const fallbackList = baseList.filter(t => t?.transaction?.type === 'admin_commission_credited');
          const totalTransactions = allStats?.total ?? fallbackList.length;
          const totalAmount = allStats?.amount ?? fallbackList.reduce((s, t) => s + (Number(t?.transaction?.amount) || 0), 0);
          const totalCommissions = allStats?.commissions ?? fallbackList.length;
          const withdrawals = allStats?.withdrawals ?? 0;
          return (
            <div className="px-6 pt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-gray-50 border">
                <div className="text-sm text-gray-600">Total Transactions</div>
                <div className="text-2xl font-bold text-gray-900">{totalTransactions}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 border">
                <div className="text-sm text-gray-600">Total Amount</div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 border">
                <div className="text-sm text-gray-600">Commissions</div>
                <div className="text-2xl font-bold text-gray-900">{totalCommissions}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 border">
                <div className="text-sm text-gray-600">Withdrawals</div>
                <div className="text-2xl font-bold text-gray-900">{withdrawals}</div>
              </div>
            </div>
          );
        })()}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">
              Recent Wallet Transactions
            </h3>
            <div className="flex items-center space-x-3">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              <button className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm transition-colors">
                <FaFilter className="mr-1" />
                Filter
              </button>
            </div>
          </div>
        </div>

        {(() => {
          const rawList = Array.isArray(walletTransactions)
            ? walletTransactions
            : (walletTransactions?.transactions || []);
          const displayList = rawList.filter((item) => {
            const method = (item?.transaction?.paymentMethod || item?.transaction?.paymentMode || '').toLowerCase();
            const type = item?.transaction?.type;
            if (method === 'cod') {
              if (type === 'ride_payment_received' || type === 'vendor_commission_credited') {
                return false;
              }
            }
            return true;
          });
          return (
            <RecentTransactionsTable
              transactions={displayList}
              isLoading={isLoading}
              defaultFilter="admin_commission_credited"
            />
          );
        })()}

        {(() => {
          const totalItems = walletTransactions?.pagination?.total ?? 0;
          const totalPages = totalItems > 0 ? Math.ceil(totalItems / limit) : 0;

          // When there are no items, don't render pagination
          if (totalItems === 0) {
            return (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                Showing 0 entries
              </div>
            );
          }

          return (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) =>
                  setPage(Math.max(1, Math.min(p, totalPages)))
                }
                totalItems={totalItems}
                itemsPerPage={limit}
                showWhenSinglePage={true}
              />
            </div>
          );
        })()}


      </div>
    </div>
  );
};

export default WalletSummary;
